import dayjs from 'dayjs';
import { useState } from 'react';
import { TFunction } from 'i18next';
import { CalendarDayInfoType, EventType, Months, WeekDayAbbrType, WeekDayType } from '../../CalendarTypes';

export const months = [
  Months.JAN,
  Months.FEB,
  Months.MAR,
  Months.APR,
  Months.MAY,
  Months.JUNE,
  Months.JULY,
  Months.AUG,
  Months.SEP,
  Months.OCT,
  Months.NOV,
  Months.DEC,
];
export const weekDays = [
  WeekDayAbbrType.MON,
  WeekDayAbbrType.TUE,
  WeekDayAbbrType.WED,
  WeekDayAbbrType.THU,
  WeekDayAbbrType.FRI,
  WeekDayAbbrType.SAT,
  WeekDayAbbrType.SUN,
];

export const daysAndMonthsWithLeadingZero = (dateNumber: number) => {
  return dateNumber < 10 ? `0${dateNumber.toString()}` : dateNumber;
};

export const normalizeDate = (dateString: string): string => {
  return dayjs(dateString).format('YYYY-MM-DD');
};

const daysCountOfMonth = (year: number, month: number): number => new Date(year, month, 0).getDate();

export function useCalendar(events: EventType[], t: TFunction) {
  const today = dayjs();
  const [currentYear, setCurrentYear] = useState(today.year());
  const [currentMonth, setCurrentMonth] = useState(today.month() + 1);
  const [eventsToShow, setEventsToShow] = useState<EventType[]>([]);

  const monthStr = daysAndMonthsWithLeadingZero(currentMonth);
  const firstDayOfCurrentMonth = dayjs(`${currentYear}-${monthStr}-01`).locale('en').format('ddd') as WeekDayType;

  const getEventsCountOfDay = (day: number, month: number, year: number): number => {
    let eventsCount = 0;
    events.forEach((eventItem: EventType) => {
      const eventDate = dayjs(eventItem.date);
      if (year === eventDate.year() && month === eventDate.month() + 1 && day === eventDate.date()) {
        eventsCount += 1;
      }
    });
    return eventsCount;
  };

  const setPreviousMonthAndYear = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const setNextMonthAndYear = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const getFirstPositionOfMonth = (): number => {
    switch (firstDayOfCurrentMonth) {
      case 'Mon':
        return 1;
      case 'Tue':
        return 2;
      case 'Wed':
        return 3;
      case 'Thu':
        return 4;
      case 'Fri':
        return 5;
      case 'Sat':
        return 6;
      default:
        return 7;
    }
  };

  const isCurrentDate = (day: number): boolean => {
    return day === today.date() &&
      currentMonth === today.month() + 1 &&
      currentYear === today.year();
  };

  const getCalendarDayInfo = (position: number): CalendarDayInfoType => {
    const daysCountOfCurrentMonth = daysCountOfMonth(currentYear, currentMonth);
    const firstPositionOfMonth = getFirstPositionOfMonth();
    let day = position;
    // previous month
    if (firstPositionOfMonth > position) {
      const daysCountOfPreviousMonth = daysCountOfMonth(currentYear, currentMonth - 1);
      day = daysCountOfPreviousMonth - (firstPositionOfMonth - position - 1);
      const eventsCount = getEventsCountOfDay(day, currentMonth - 1, currentYear);
      const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;
      return {
        day,
        eventsCount,
        date: `${currentYear}-${daysAndMonthsWithLeadingZero(previousMonth)}-${daysAndMonthsWithLeadingZero(day)}`,
        isCurrentMonth: false,
        isCurrentDay: false,
      };
    }
    // current month
    if (position >= firstPositionOfMonth && position - firstPositionOfMonth < daysCountOfCurrentMonth) {
      day = position - firstPositionOfMonth + 1;
      const eventsCount = getEventsCountOfDay(day, currentMonth, currentYear);
      return {
        day,
        eventsCount,
        date: `${currentYear}-${daysAndMonthsWithLeadingZero(currentMonth)}-${daysAndMonthsWithLeadingZero(day)}`,
        isCurrentMonth: true,
        isCurrentDay: isCurrentDate(day),
      };
    }
    // next month
    if (position - firstPositionOfMonth >= daysCountOfCurrentMonth) {
      day = position - firstPositionOfMonth + 1 - daysCountOfCurrentMonth;
      const eventsCount = getEventsCountOfDay(day, currentMonth + 1, currentYear);
      const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
      return {
        day,
        eventsCount,
        date: `${currentYear}-${daysAndMonthsWithLeadingZero(nextMonth)}-${daysAndMonthsWithLeadingZero(day)}`,
        isCurrentMonth: false,
        isCurrentDay: false,
      };
    }
    const eventsCount = getEventsCountOfDay(day, currentMonth, currentYear);
    return {
      day,
      eventsCount,
      date: `${currentYear}-${daysAndMonthsWithLeadingZero(currentMonth)}-${daysAndMonthsWithLeadingZero(day)}`,
      isCurrentMonth: true,
      isCurrentDay: isCurrentDate(day),
    };
  };

  const onClickEvent = (currentDate: string) => {
    const eventsOfDay: EventType[] = [];
    events.forEach((eventItem: EventType) => {
      if (currentDate === normalizeDate(eventItem.date)) {
        eventsOfDay.push(eventItem);
      }
    });
    setEventsToShow(eventsOfDay);
  };

  const getClassOfCalendarDay = (calendarDayInfo: CalendarDayInfoType): any => {
    if (calendarDayInfo.isCurrentMonth) {
      if (calendarDayInfo.isCurrentDay) {
        return ' calendar__tabledata--today';
      }
      return ' calendar__tabledata--current-month';
    }
    return '';
  };

  const weekday = (day: string): WeekDayType => {
    return (day.charAt(0).toUpperCase() + day.slice(1)) as WeekDayType;
  };

  const parseSelectedDate = (dateOfEvent: string): string => {
    const selectedDate = dayjs(dateOfEvent);
    const week = t(`weekDays.${weekday(selectedDate.locale('en').format('ddd') as WeekDayType)}`);
    const day = selectedDate.date();
    const month = t(`months.${months[selectedDate.month()]}`);
    const year = selectedDate.year();
    return `${week}, ${day}.${month} ${year}`;
  };

  return {
    currentYear,
    currentMonth,
    eventsToShow,
    setEventsToShow,
    setPreviousMonthAndYear,
    setNextMonthAndYear,
    getCalendarDayInfo,
    onClickEvent,
    getClassOfCalendarDay,
    parseSelectedDate,
    weekday,
  };
}
